/*
 * Typed wrappers over the sales claim endpoints the form and the list share.
 */

import { get_api as getApi, post_api as postApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import { fetch_me, fetch_roster } from '../sales_team/api';
import type { SalesMe, TeamMember } from '../sales_team/api';
import type { AssignResult, ClaimStatusPoll } from './claim_hold';

const BASE = '/v2/sales_task_list';

interface Envelope<T> {
  data?: { data?: T };
}

export const unclaim_task = (uuid: string): Promise<void> =>
  postApi(getApiUrl(`${BASE}/unclaim`), { uuid }, true).then(() => undefined);

export const assign_task = (uuid: string, ops_uuid: string): Promise<AssignResult> =>
  postApi(getApiUrl(`${BASE}/assign`), { uuid, ops_uuid }, true).then(
    (res: Envelope<AssignResult>) => res?.data?.data || {}
  );

export interface SendTaskChatResult {
  message_id?: string;
  sent_at_iso?: string;
}

// message_id is the client's uuid v4, reused by the socket fallback so a send
// that timed out after publishing is not delivered twice.
export const send_task_chat = (
  task_uuid: string,
  message: string,
  message_id: string
): Promise<SendTaskChatResult> =>
  postApi(getApiUrl(`${BASE}/send_chat`), { task_uuid, message, message_id }, true).then(
    (res: Envelope<SendTaskChatResult>) => res?.data?.data || {}
  );

export const fetch_claim_status = (uuid: string): Promise<ClaimStatusPoll> =>
  getApi(getApiUrl(`${BASE}/claim_status/${encodeURIComponent(uuid)}`), true).then(
    (res: Envelope<ClaimStatusPoll>) => res?.data?.data || {}
  );

// One /me per page session; a failure is not cached.
let me_promise: Promise<SalesMe> | null = null;
export const fetch_me_once = (): Promise<SalesMe> => {
  if (!me_promise) {
    me_promise = fetch_me().catch((e: unknown) => {
      me_promise = null;
      throw e;
    });
  }
  return me_promise;
};

export const ROSTER_CACHE_MS = 60 * 1000;
let roster_cache: { at: number; members: Promise<TeamMember[]> } | null = null;

export const get_roster = (now: number = Date.now()): Promise<TeamMember[]> => {
  if (roster_cache && now - roster_cache.at < ROSTER_CACHE_MS) return roster_cache.members;
  const members = fetch_roster().then((r) => r.members);
  const entry = { at: now, members };
  roster_cache = entry;
  members.catch(() => {
    if (roster_cache === entry) roster_cache = null;
  });
  return members;
};

export const reset_claim_api_caches = (): void => {
  me_promise = null;
  roster_cache = null;
};
